import { useRef } from 'react';
import { useMutation } from '@apollo/client';
// Api
import {
  APPROVE_TICKET_ZERO_TAX,
  CREATE_TICKET_EVENT,
  SET_TICKET_SALE_STATUS,
} from 'api/ticketEvents/mutations';
// Types
import {
  CreateTicketEventInput,
  TicketEventStatus,
} from 'api/graphql-global-types';
import {
  CreateTicketEvent,
  CreateTicketEventVariables,
} from 'api/ticketEvents/types/CreateTicketEvent';
import {
  ApproveTicketZeroTax,
  ApproveTicketZeroTaxVariables,
} from 'api/ticketEvents/types/ApproveTicketZeroTax';
import {
  SetTicketSaleStatus,
  SetTicketSaleStatusVariables,
} from 'api/ticketEvents/types/SetTicketSaleStatus';
import { HostedTicketEventFields } from 'api/ticketEvents/types/HostedTicketEventFields';

type TicketEventVersion = { id: string; version: number };

type OpenTicketSales = {
  /** The event at its version after the approval; the same event when nothing came back. */
  approveZeroTax: (event: TicketEventVersion) => Promise<TicketEventVersion>;
  putOnSale: (
    event: TicketEventVersion
  ) => Promise<HostedTicketEventFields | null>;
};

/** Tickets need their tax policy before an event goes on sale, so a draft is approved first. */
export const useOpenTicketSales = (): OpenTicketSales => {
  const [approveTicketZeroTax] = useMutation<
    ApproveTicketZeroTax,
    ApproveTicketZeroTaxVariables
  >(APPROVE_TICKET_ZERO_TAX);
  const [setTicketSaleStatus] = useMutation<
    SetTicketSaleStatus,
    SetTicketSaleStatusVariables
  >(SET_TICKET_SALE_STATUS, {
    // The status filter and counts change, so the list loads again.
    update(cache) {
      cache.evict({ id: 'ROOT_QUERY', fieldName: 'hostedTicketEvents' });
    },
  });

  return {
    approveZeroTax: async (event) => {
      const { data } = await approveTicketZeroTax({
        variables: {
          input: { ticketEventId: event.id, expectedVersion: event.version },
        },
      });

      return data
        ? {
            id: data.approveTicketZeroTax.id,
            version: data.approveTicketZeroTax.version,
          }
        : event;
    },
    putOnSale: async (event) => {
      const { data } = await setTicketSaleStatus({
        variables: {
          input: {
            ticketEventId: event.id,
            expectedVersion: event.version,
            status: TicketEventStatus.OnSale,
          },
        },
      });

      return data?.setTicketSaleStatus ?? null;
    },
  };
};

type PublishTicketEvent = {
  /** True once the event exists, so a retry does not create it twice. */
  hasCreatedEvent: () => boolean;
  /** Creates the event once, approves its zero tax once, then puts it on sale. */
  publishTicketEvent: (
    buildInput: () => CreateTicketEventInput | Promise<CreateTicketEventInput>
  ) => Promise<HostedTicketEventFields | null>;
};

export const usePublishTicketEvent = (): PublishTicketEvent => {
  // A retry after a partial failure carries on from the last step that went through.
  const createdEventRef = useRef<TicketEventVersion | null>(null);
  const zeroTaxApprovedRef = useRef(false);
  const { approveZeroTax, putOnSale } = useOpenTicketSales();

  const [createTicketEvent] = useMutation<
    CreateTicketEvent,
    CreateTicketEventVariables
  >(CREATE_TICKET_EVENT);

  const publishTicketEvent: PublishTicketEvent['publishTicketEvent'] = async (
    buildInput
  ) => {
    if (!createdEventRef.current) {
      const { data } = await createTicketEvent({
        variables: { input: await buildInput() },
      });

      if (data) {
        const { id, version } = data.createTicketEvent;
        createdEventRef.current = { id, version };
      }
    }

    if (!createdEventRef.current) {
      return null;
    }

    if (!zeroTaxApprovedRef.current) {
      createdEventRef.current = await approveZeroTax(createdEventRef.current);
      zeroTaxApprovedRef.current = true;
    }

    return putOnSale(createdEventRef.current);
  };

  return {
    hasCreatedEvent: () => Boolean(createdEventRef.current),
    publishTicketEvent,
  };
};
